'use client';

import PropTypes from 'prop-types';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
  const quantity = item?.quantity || item?.minOrderQty || 1;

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < item?.minOrderQty) return;
    if (newQuantity > item?.stock) return;

    await onUpdateQuantity(item?.id, newQuantity);
  };

  const handleIncrement = () => {
    if (quantity < item?.stock) handleQuantityChange(quantity + 1);
  };

  const handleDecrement = () => {
    if (quantity > item?.minOrderQty) handleQuantityChange(quantity - 1);
  };

  const handleInputChange = (e) => {
    const value = parseInt(e?.target?.value);
    if (!value) return;
    handleQuantityChange(value);
  };

  const lineTotal = item?.price * quantity;
  const stockStatus =
    item?.stock > 10 ? 'In Stock' : item?.stock > 0 ? 'Low Stock' : 'Out of Stock';
  const stockColor =
    item?.stock > 10 ? 'text-success' : item?.stock > 0 ? 'text-warning' : 'text-error';

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-md transition-smooth">
      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-12 gap-4 md:gap-6 items-center">
        {/* Product Image */}
        <div className="col-span-2">
          <Link
            href={`/product-details?id=${item?.id}`}
            className="block aspect-square overflow-hidden rounded-md bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <AppImage
              src={item?.image}
              alt={item?.alt}
              className="w-full h-full object-cover hover:scale-105 transition-smooth"
            />
          </Link>
        </div>

        {/* Product Details */}
        <div className="col-span-4">
          <Link href={`/product-details?id=${item?.id}`} className="focus:outline-none focus:ring-2 focus:ring-ring rounded">
            <h3 className="text-base lg:text-lg font-semibold text-foreground mb-1 line-clamp-2 hover:text-primary transition-smooth">
              {item?.name}
            </h3>
          </Link>
          <p className="text-sm caption text-muted-foreground mb-2">
            Part No: <span className="data-text font-medium">{item?.partNumber}</span>
          </p>
          <div className="flex items-center gap-2">
            <Icon name="MapPinIcon" size={16} className="text-muted-foreground" />
            <span className="text-xs caption text-muted-foreground">{item?.location}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs caption font-medium ${stockColor}`}>{stockStatus}</span>
            <span className="text-xs caption text-muted-foreground">({item?.stock} available)</span>
          </div>
        </div>

        {/* Price */}
        <div className="col-span-2 text-center">
          <p className="text-lg lg:text-xl font-semibold text-foreground data-text">
            ${item?.price?.toLocaleString('en-US')}
          </p>
          <p className="text-xs caption text-muted-foreground">per unit</p>
        </div>

        {/* Quantity Controls */}
        <div className="col-span-2">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={handleDecrement}
              disabled={quantity <= item?.minOrderQty}
              className="w-8 h-8 flex items-center justify-center bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Decrease quantity"
            >
              <Icon name="MinusIcon" size={14} />
            </button>

            <input
              type="number"
              value={quantity}
              onChange={handleInputChange}
              min={item?.minOrderQty}
              max={item?.stock}
              className="w-16 h-8 text-center border border-border rounded-md text-sm data-text font-medium focus:outline-none focus:ring-2 focus:ring-ring"
            />

            <button
              onClick={handleIncrement}
              disabled={quantity >= item?.stock}
              className="w-8 h-8 flex items-center justify-center bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Increase quantity"
            >
              <Icon name="PlusIcon" size={14} />
            </button>
          </div>

          <p className="text-xs caption text-muted-foreground text-center mt-1">
            Min: {item?.minOrderQty}
          </p>
        </div>

        {/* Line Total + Remove */}
        <div className="col-span-2 flex items-center justify-between min-w-[140px]">
          <p className="text-lg lg:text-xl font-bold text-primary data-text whitespace-nowrap">
            ${lineTotal?.toLocaleString('en-US')}
          </p>
          <button
            onClick={() => onRemove(item?.id)}
            className="p-2 text-error hover:bg-error/10 rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Remove item"
          >
            <Icon name="TrashIcon" size={18} />
          </button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="flex gap-4">
          <div className="w-24 h-24 flex-shrink-0">
            <Link
              href={`/product-details?id=${item?.id}`}
              className="block aspect-square overflow-hidden rounded-md bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <AppImage src={item?.image} alt={item?.alt} className="w-full h-full object-cover" />
            </Link>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <Link href={`/product-details?id=${item?.id}`} className="flex-1 focus:outline-none focus:ring-2 focus:ring-ring rounded">
                <h3 className="text-sm font-semibold text-foreground line-clamp-2 hover:text-primary transition-smooth">
                  {item?.name}
                </h3>
              </Link>

              <button
                onClick={() => onRemove(item?.id)}
                className="p-1 text-error hover:bg-error/10 rounded-md transition-smooth flex-shrink-0"
                aria-label="Remove item"
              >
                <Icon name="TrashIcon" size={18} />
              </button>
            </div>

            <p className="text-xs caption text-muted-foreground mb-1">
              Part: <span className="data-text">{item?.partNumber}</span>
            </p>

            <div className="flex items-center gap-1 mb-1">
              <Icon name="MapPinIcon" size={14} className="text-muted-foreground" />
              <span className="text-xs caption text-muted-foreground">{item?.location}</span>
            </div>

            <span className={`text-xs caption font-medium ${stockColor}`}>{stockStatus}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm caption text-muted-foreground">Unit Price:</span>
            <span className="text-base font-semibold text-foreground data-text">
              ${item?.price?.toLocaleString('en-US')}
            </span>
          </div>

          <div className="flex items-center justify-between mb-3">
            <span className="text-sm caption text-muted-foreground">Quantity:</span>

            <div className="flex items-center gap-2">
              <button
                onClick={handleDecrement}
                disabled={quantity <= item?.minOrderQty}
                className="w-8 h-8 flex items-center justify-center bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-smooth"
              >
                <Icon name="MinusIcon" size={16} />
              </button>

              <input
                type="number"
                value={quantity}
                onChange={handleInputChange}
                min={item?.minOrderQty}
                max={item?.stock}
                className="w-14 h-8 text-center border border-border rounded-md text-sm data-text font-medium focus:outline-none focus:ring-2 focus:ring-ring"
              />

              <button
                onClick={handleIncrement}
                disabled={quantity >= item?.stock}
                className="w-8 h-8 flex items-center justify-center bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-smooth"
              >
                <Icon name="PlusIcon" size={16} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border">
            <span className="text-sm font-medium text-foreground">Total:</span>
            <span className="text-lg font-bold text-primary data-text">
              ${lineTotal?.toLocaleString('en-US')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

CartItem.propTypes = {
  item: PropTypes?.shape({
    id: PropTypes?.oneOfType([PropTypes?.string, PropTypes?.number])?.isRequired,
    name: PropTypes?.string?.isRequired,
    partNumber: PropTypes?.string?.isRequired,
    image: PropTypes?.string?.isRequired,
    alt: PropTypes?.string?.isRequired,
    price: PropTypes?.number?.isRequired,
    quantity: PropTypes?.number?.isRequired,
    stock: PropTypes?.number?.isRequired,
    minOrderQty: PropTypes?.number?.isRequired,
    location: PropTypes?.string?.isRequired,
  })?.isRequired,
  onUpdateQuantity: PropTypes?.func?.isRequired,
  onRemove: PropTypes?.func?.isRequired,
};

export default CartItem;
